import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import { Formik } from 'formik';

import { distances, colors } from '../../../styles/constants';
import Modal from '../../../components/Modal';
import { H2 } from '../../../components/Typography';
import { Button, ButtonGroup } from '../../../components/Buttons';
import { UiState } from '../types';
import { LoadingOverlay } from '../../../components/Loading';
import { formatMoney } from '../../../helpers/formatters';
import { Table, Thead, Tr, Th, Tbody } from '../../../components/Table';

import ItemRow from './ItemRow';
import { LineItem, Order } from '../../../types/orders';
import { getCaptureableItems } from '../../helpers';
import { OperationLineItem, AuthorizationGroup } from '../../types';

export interface ByItemsProps extends WithTranslation {
    state: UiState;
    order: Order;
    authorizationGroup: AuthorizationGroup;
    dismissModal: () => void;
    captureItems: (accountId: string, order: Order, items: OperationLineItem[], authorizationId: string | undefined) => void;
    accountId: string;
}

const getAmount = (items: OperationLineItem[]) => {
    return items.reduce((sum, item) => (item.amount || 0) + sum, 0);
};

const checkAllItemsSelected = (operationLineItems: OperationLineItem[], items: LineItem[]) => {
    return operationLineItems.filter((x) => x.amount).length === items.length;
};

const ByItems = (
    {
        state,
        order,
        authorizationGroup,
        dismissModal,
        captureItems,
        t,
        i18n,
        accountId,
    }: ByItemsProps
) => {
    const lng = i18n.language;
    const items = getCaptureableItems(authorizationGroup);
    const initialValues: { items: OperationLineItem[] } = { items: []};
    return (
        <Modal width="70%">
            <Wrapper>
                <Formik
                    initialValues={initialValues}
                    validate={(values) => {
                        if (values.items.length === 0) {
                            return { items: ['error']};
                        }
                        const itemsErrors = values.items
                            .map((i) => (i.amount > (i.item.net_amount || 0) ? 'invalid' : ''))
                            .filter((x) => x);
                        if (itemsErrors.length) {
                            return { items: itemsErrors };
                        }
                        return undefined;
                    }}
                    onSubmit={(values) => {
                        captureItems(accountId, order, values.items, authorizationGroup.authorization.id);
                    }}
                >
                    {({ values, errors, handleSubmit, setFieldValue }) => {
                        const toggleAllItems = (value: boolean) => {
                            let changedItems: OperationLineItem[] = [];
                            if (value) {
                                changedItems = items.map((item) => ({
                                    amount: item.net_amount || 0,
                                    item,
                                }));
                            }
                            setFieldValue('items', changedItems);
                        };

                        const toggleItem = (item: LineItem, amount: number) => {
                            const changedItems = [...values.items].filter((o) => o.item.line_id !== item.line_id);
                            if (amount > 0) {
                                changedItems.push({
                                    amount,
                                    item,
                                });
                            }
                            setFieldValue('items', changedItems);
                        };
                        return (
                            <form onSubmit={handleSubmit} autoComplete="nope">
                                <HiddenInput autoComplete="false" name="hidden" type="text" />
                                <H2>{t('orders.capture.title')}</H2>

                                <InfoBox>{t('orders.capture.manual_operation_description')}</InfoBox>

                                <Items>
                                    <Table>
                                        <Thead>
                                            <Tr>
                                                <Th />
                                                <Th>{t('orders.capture.table.line_id')}</Th>
                                                <Th>{t('orders.capture.table.description')}</Th>
                                                <Th>{t('orders.capture.table.details')}</Th>
                                                <Th>{t('orders.capture.table.quantity')}</Th>
                                                <Th className="align-right">{t('orders.capture.table.net_amount')}</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {items.map((item, i) => (
                                                <ItemRow
                                                    order={order}
                                                    item={item}
                                                    operationItem={values.items.find(
                                                        (o) => o.item.line_id === item.line_id
                                                    )}
                                                    checked={
                                                        values.items.findIndex(
                                                            (o) => o.item.line_id === item.line_id
                                                        ) >= 0
                                                    }
                                                    key={item.line_id}
                                                    onChange={toggleItem}
                                                />
                                            ))}
                                        </Tbody>
                                    </Table>
                                </Items>
                                {checkAllItemsSelected(values.items, items) ? (
                                    <AllToggle onClick={() => toggleAllItems(false)}>
                                        {t('orders.capture.form.select_no_items')}
                                    </AllToggle>
                                ) : (
                                    <AllToggle onClick={() => toggleAllItems(true)}>
                                        {t('orders.capture.form.select_all_items')}
                                    </AllToggle>
                                )}
                                <ButtonGroup>
                                    <OperationButton
                                        type="submit"
                                        disabled={
                                            values.items.length === 0 || ((errors && errors.items) || []).length > 0
                                        }
                                    >
                                        {t('orders.capture.form.authorize_button', {
                                            amount: formatMoney(lng, getAmount(values.items), order.currency),
                                        })}
                                    </OperationButton>
                                    <Button className="alt" onClick={dismissModal}>
                                        {t('orders.capture.form.cancel_button')}
                                    </Button>
                                </ButtonGroup>
                            </form>
                        );
                    }}
                </Formik>
                {state === UiState.InFlight && <LoadingOverlay />}
            </Wrapper>
        </Modal>
    );
};

export default withTranslation()(ByItems);

const Wrapper = styled.div`
    position: relative;
`;

const InfoBox = styled.div`
    position: relative;
    border-left: 4px solid ${colors.primary};
    padding: 16px;
    margin-top: 16px;
    margin-bottom: 16px;
`;

const Items = styled.div`
    padding-top: ${distances.tiny};
`;

const OperationButton = styled(Button)`
    min-width: 220px;
`;

const AllToggle = styled.div`
    color: ${colors.primary};
    font-size: 11px;
    margin-bottom: ${distances.small};
    cursor: pointer;
`;

const HiddenInput = styled.input`
    display: none;
`;
